import DOMPurify from "dompurify";
import { CommentNode, CommentScopeKind } from "./types";

const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
});

export function stripCommentHtml(value: string | null | undefined): string {
  const cleaned = DOMPurify.sanitize(value || "", { ALLOWED_TAGS: [] });
  return cleaned.replace(/\s+/g, " ").trim();
}

export function getCommentExcerpt(value: string | null | undefined, maxLength = 140): string {
  const text = stripCommentHtml(value);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 1)).trim()}…`;
}

export function getCommentDisplayText(comment: CommentNode | null | undefined): string {
  if (!comment) return "";
  if (comment.isRemoved) return "This comment was removed. Replies remain visible for context.";
  return stripCommentHtml(comment.comment);
}

export function getCommentDisplayExcerpt(
  comment: CommentNode | null | undefined,
  maxLength = 140
): string {
  if (!comment) return "";
  return getCommentExcerpt(getCommentDisplayText(comment), maxLength);
}

export function getTimestamp(dateStr?: string | null): number {
  if (!dateStr) return 0;
  const parsed = Date.parse(dateStr);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function formatCommentTime(dateStr?: string | null): string {
  if (!dateStr) return "unknown time";
  const parsed = Date.parse(dateStr);
  if (Number.isNaN(parsed)) return "unknown time";

  const diff = Date.now() - parsed;
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diff < minute) return "just now";
  if (diff < hour) return `${Math.max(1, Math.floor(diff / minute))} min ago`;
  if (diff < day) return `${Math.max(1, Math.floor(diff / hour))} hours ago`;
  if (diff < 2 * day) return "yesterday";
  if (diff < 30 * day) return `${Math.floor(diff / day)} days ago`;

  return DATE_FORMATTER.format(parsed);
}

export function getCommentScopeKind(
  comment: Pick<CommentNode, "editId"> | null | undefined,
  currentEditId?: number | null
): CommentScopeKind {
  if (!comment) return "unknown";
  if (!comment.editId) return "gistWide";
  if (!currentEditId) return "unknown";
  return comment.editId === currentEditId ? "currentEdit" : "olderEdit";
}

export function getCommentScopeLabel(
  comment: Pick<CommentNode, "editId"> | null | undefined,
  currentEditId?: number | null
): string {
  const kind = getCommentScopeKind(comment, currentEditId);
  if (kind === "gistWide") return "gist-wide";
  if (kind === "currentEdit") return "current edit";
  if (kind === "olderEdit") return `older edit ${comment?.editId ?? ""}`.trim();
  return comment?.editId ? `edit ${comment.editId}` : "unknown scope";
}

export function getCommentScopeClass(
  comment: Pick<CommentNode, "editId"> | null | undefined,
  currentEditId?: number | null
): string {
  const kind = getCommentScopeKind(comment, currentEditId);
  if (kind === "gistWide") return "is-gist-wide";
  if (kind === "currentEdit") return "is-current-edit";
  if (kind === "olderEdit") return "is-older-edit";
  return "is-unknown-scope";
}

export function getActivityGroup(dateStr?: string | null): "Today" | "Yesterday" | "Older" {
  const timestamp = getTimestamp(dateStr);
  if (!timestamp) return "Older";

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterdayStart = todayStart - 24 * 60 * 60 * 1000;

  if (timestamp >= todayStart) return "Today";
  if (timestamp >= yesterdayStart) return "Yesterday";
  return "Older";
}

export function isModeratorRole(role?: string): boolean {
  return role === "admin" || role === "moderator";
}
